import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppSettings } from '../app-settings';
import {
  NotificationMessageSummary,
  NotificationMessageDetail,
  NotificationMessageFilter,
  RequeueRequest,
  TemplateSummary,
  CurrentTemplate,
  TemplateVersion,
  SchemaVar,
  SaveTemplateRequest,
  SaveTemplateResult
} from '../domain/types/notification';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private baseUrl: string;

  constructor(private http: HttpClient, private appSettings: AppSettings) {
    this.baseUrl = `${appSettings.APIURL}/notification`;
  }

  // ─── Messages ──────────────────────────────────────────────

  /** The acting admin's own email, for pre-filling the requeue redirect field.
   *  Resolved server-side (the Azure id token often carries no email claim). */
  async getMyEmail(): Promise<string> {
    const r = await firstValueFrom(
      this.http.get<{ email: string }>(`${this.baseUrl}/me`)
    );
    return r?.email ?? '';
  }

  async getMessages(filter: NotificationMessageFilter): Promise<NotificationMessageSummary[]> {
    let params = new HttpParams();
    if (filter.status)            params = params.set('status', filter.status);
    if (filter.caseId != null)    params = params.set('caseId', String(filter.caseId));
    if (filter.recipient)         params = params.set('recipient', filter.recipient);
    if (filter.from)              params = params.set('from', filter.from);
    if (filter.to)                params = params.set('to', filter.to);
    if (filter.limit != null)     params = params.set('limit', String(filter.limit));
    if (filter.beforeCreatedAt)   params = params.set('beforeCreatedAt', filter.beforeCreatedAt);
    if (filter.beforeId)          params = params.set('beforeId', filter.beforeId);
    return firstValueFrom(
      this.http.get<NotificationMessageSummary[]>(`${this.baseUrl}/messages`, { params })
    );
  }

  async getMessage(id: string): Promise<NotificationMessageDetail> {
    return firstValueFrom(
      this.http.get<NotificationMessageDetail>(`${this.baseUrl}/messages/${id}`)
    );
  }

  /** Requeue a Held/Failed message. Returns the updated row. */
  async requeueMessage(id: string, req: RequeueRequest): Promise<NotificationMessageDetail> {
    return firstValueFrom(
      this.http.post<NotificationMessageDetail>(`${this.baseUrl}/messages/${id}/requeue`, req)
    );
  }

  /**
   * The most recent message sent from a given template, or null if none exists.
   * Used to populate the editor's sample inputs from a real send. Two calls
   * because the summary row doesn't carry TemplateDataJSON: list (server-filtered
   * by templateName, newest first, limit 1) then fetch that row's full detail.
   */
  async getLatestMessageForTemplate(templateName: string): Promise<NotificationMessageDetail | null> {
    let params = new HttpParams().set('templateName', templateName).set('limit', '1');
    const rows = await firstValueFrom(
      this.http.get<NotificationMessageSummary[]>(`${this.baseUrl}/messages`, { params })
    );
    if (!rows.length) return null;
    return this.getMessage(rows[0].id);
  }

  // ─── Templates ─────────────────────────────────────────────

  async listTemplates(): Promise<TemplateSummary[]> {
    return firstValueFrom(this.http.get<TemplateSummary[]>(`${this.baseUrl}/templates`));
  }

  async getTemplate(name: string): Promise<CurrentTemplate> {
    return firstValueFrom(this.http.get<CurrentTemplate>(`${this.baseUrl}/templates/${name}`));
  }

  async getTemplateSchema(name: string): Promise<SchemaVar[]> {
    return firstValueFrom(this.http.get<SchemaVar[]>(`${this.baseUrl}/templates/${name}/schema`));
  }

  async getTemplateHistory(name: string): Promise<TemplateVersion[]> {
    return firstValueFrom(this.http.get<TemplateVersion[]>(`${this.baseUrl}/templates/${name}/history`));
  }

  async getTemplateVersion(name: string, version: number): Promise<TemplateVersion> {
    return firstValueFrom(
      this.http.get<TemplateVersion>(`${this.baseUrl}/templates/${name}/versions/${version}`)
    );
  }

  /**
   * Save a template. On HTTP 409 (another admin saved first) the server returns
   * the latest template in the error body; this resolves to a SaveTemplateResult
   * with `conflict: true` carrying that state, rather than throwing, so the UI
   * can prompt to reload. Other HTTP errors still reject.
   */
  async saveTemplate(name: string, req: SaveTemplateRequest): Promise<SaveTemplateResult> {
    try {
      const saved = await firstValueFrom(
        this.http.put<CurrentTemplate>(`${this.baseUrl}/templates/${name}`, req)
      );
      return { conflict: false, template: saved };
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 409) {
        return { conflict: true, template: err.error as CurrentTemplate };
      }
      throw err;
    }
  }
}
